import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Paper,
  Container,
  CircularProgress,
  Alert,
  Button,
  TextField,
  FormControl,
  FormHelperText,
  Select,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Chip,
  IconButton,
  useTheme,
  useMediaQuery,
  AppBar,
  Toolbar
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Description as DescriptionIcon,
  Assessment as AssessmentIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  AttachFile as AttachFileIcon,
  Image as ImageIcon,
  PictureAsPdf as PictureAsPdfIcon,
  TableChart as TableChartIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import CalculationField from '../../components/form-builder/fields/CalculationField';

// API base URL ve headers'ı sabit olarak tanımla
const API_BASE_URL = 'https://app.hhoms.com/api';
const API_HEADERS = {
  'Content-Type': 'application/json',
  'ApplicationToken': '7db7cfd1-6ea2-487d-8f0a-a830e31eff94'
};

const FormResponseDetail = () => {
  const navigate = useNavigate();
  const { responseId } = useParams(); // responseId olarak al
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Redux'tan kullanıcı bilgilerini al
  const userData = useSelector((state) => {
    const reduxData = state?.loginData?.loginData;
    
    if (!reduxData || !reduxData.id) {
      // Önce form kullanıcısı verilerini kontrol et
      const formUserData = localStorage.getItem('formUserData');
      if (formUserData) {
        try {
          return JSON.parse(formUserData);
        } catch (error) {
          console.error('Error parsing localStorage formUserData:', error);
        }
      }
      
      // Eğer form kullanıcısı verisi yoksa, panel kullanıcısı verilerini kontrol et
      const localUserData = localStorage.getItem('userData');
      if (localUserData) {
        try {
          return JSON.parse(localUserData);
        } catch (error) {
          console.error('Error parsing localStorage userData:', error);
        }
      }
    }
    
    return reduxData;
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [responseData, setResponseData] = useState(null);
  const [formContent, setFormContent] = useState(null);
  const [formData, setFormData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    const loadResponseData = async () => {
      try {
        console.log('Yanıt detayı yükleniyor, responseId:', responseId);
        
        // Önce localStorage'dan veri almayı dene
        const currentResponseId = localStorage.getItem('currentResponseId');
        const currentResponseData = localStorage.getItem('currentResponseData');
        const currentFormContent = localStorage.getItem('currentFormContent');
        const currentFormData = localStorage.getItem('currentFormData');
        const currentFormTitle = localStorage.getItem('currentFormTitle');
        
        if (currentResponseId && currentFormContent && currentFormData) {
          console.log('localStorage\'dan veri alınıyor...');
          
          try {
            // formContent'ı parse et
            let formContentObj = {};
            if (typeof currentFormContent === 'string') {
              try {
                formContentObj = JSON.parse(currentFormContent);
              } catch (parseError) {
                console.error('formContent parse hatası:', parseError);
                formContentObj = JSON.parse(currentFormContent);
              }
            } else {
              formContentObj = currentFormContent;
            }

            // formData'yı parse et
            let formDataObj = {};
            if (typeof currentFormData === 'string') {
              try {
                formDataObj = JSON.parse(currentFormData);
              } catch (parseError) {
                console.error('formData parse hatası:', parseError);
                formDataObj = JSON.parse(currentFormData);
              }
            } else {
              formDataObj = currentFormData;
            }

            console.log('Parse edilmiş formContentObj:', formContentObj);
            console.log('Parse edilmiş formDataObj:', formDataObj);

            setFormContent(formContentObj);
            setResponseData({
              id: currentResponseId,
              title: currentFormTitle || formContentObj.title || 'Form Yanıtı',
              createDate: new Date().toISOString(),
              formData: formDataObj
            });

            // Form verilerini düzenleme için hazırla
            const fieldsWithValues = (formContentObj.fields || []).map(field => {
              // Önce formData.formData içinde ara (yeni format)
              let fieldData = formDataObj.formData?.[field.id];
              // Eğer bulunamazsa, direkt formData içinde ara (eski format)
              if (!fieldData && formDataObj[field.id]) {
                fieldData = formDataObj[field.id];
              }
              let fieldValue = '';
              
              if (field.type === 'name') {
                fieldValue = {
                  firstName: fieldData?.value?.firstName || '',
                  lastName: fieldData?.value?.lastName || ''
                };
              } else if (field.type === 'table') {
                // Tablo alanı için özel işlem
                if (fieldData?.value && fieldData.value.rows) {
                  fieldValue = fieldData.value;
                } else {
                  fieldValue = { rows: [] };
                }
              } else if (field.type === 'signature') {
                // İmza alanı için özel işlem - farklı formatları kontrol et
                fieldValue = fieldData?.value || 
                            formDataObj.signature || 
                            formDataObj.formData?.signature ||
                            formDataObj[field.id]?.value ||
                            '';
              } else {
                fieldValue = fieldData?.value || '';
              }
              
              return {
                ...field,
                value: fieldValue,
                label: field.label || '',
                type: field.type || 'text',
                options: field.options || [],
                required: field.required || false,
                placeholder: field.placeholder || '',
                helpText: field.helpText || '',
                size: field.size || '12'
              };
            });

            setFormData({
              id: currentResponseId,
              formId: formContentObj.id?.toString() || '',
              formTitle: currentFormTitle || formContentObj.title || 'Form Yanıtı',
              formDescription: formContentObj.description || '',
              formFields: fieldsWithValues,
              settings: formContentObj.settings || {}
            });
            
            setLoading(false);
            return;
          } catch (localStorageError) {
            console.error('localStorage\'dan veri alınırken hata:', localStorageError);
          }
        }
        
        // API'den yanıt verilerini çek
        let apiResponse;
        let apiData;
        
        try {
          // İlk endpoint'i dene
          apiResponse = await axios.get(`${API_BASE_URL}/form/LibraryRecords/${responseId}`, {
            headers: API_HEADERS,
            timeout: 10000
          });
          apiData = apiResponse.data;
        } catch (firstError) {
          console.log('İlk endpoint başarısız, ikinci endpoint deneniyor...');
          // İkinci endpoint'i dene
          apiResponse = await axios.get(`${API_BASE_URL}/form/LibraryRecords/list/${responseId}`, {
            headers: API_HEADERS,
            timeout: 10000
          });
          apiData = apiResponse.data;
        }
        
        console.log('API yanıtı:', apiData);
        
        if (apiData?.formLibraryRecords && Array.isArray(apiData.formLibraryRecords)) {
          console.log('formLibraryRecords bulundu, yanıt aranıyor...');
          console.log('Mevcut yanıtlar:', apiData.formLibraryRecords.map(r => ({ id: r.id, formId: r.formId, title: r.title })));
          
          // Response ID'sine göre doğru yanıtı bul
          const apiResponseData = apiData.formLibraryRecords.find(r => r.id === parseInt(responseId));
          
          if (apiResponseData) {
            console.log('API\'den güncel yanıt bulundu:', apiResponseData);
            
            // formContent ve formData'yı parse et
            let formContentObj = {};
            let formDataObj = {};

            if (apiResponseData.formContent) {
              try {
                if (typeof apiResponseData.formContent === 'string') {
                  // JSON string'i direkt parse et
                  formContentObj = JSON.parse(apiResponseData.formContent);
                } else {
                  formContentObj = apiResponseData.formContent;
                }
                setFormContent(formContentObj);
                console.log('Parse edilmiş formContent:', formContentObj);
              } catch (parseError) {
                console.error('API form içeriği parse edilemedi:', parseError);
                console.error('Raw formContent:', apiResponseData.formContent);
              }
            }

            if (apiResponseData.formData) {
              try {
                if (typeof apiResponseData.formData === 'string') {
                  // JSON string'i direkt parse et
                  formDataObj = JSON.parse(apiResponseData.formData);
                } else {
                  formDataObj = apiResponseData.formData;
                }
                console.log('Parse edilmiş formData:', formDataObj);
              } catch (e) {
                console.error('formData parse edilemedi:', e);
                console.error('Raw formData:', apiResponseData.formData);
              }
            }
            
            setResponseData(apiResponseData);

            // Form verilerini düzenleme için hazırla
            const fieldsWithValues = (formContentObj.fields || []).map(field => {
              // formData.formData içinde alan verisini ara
              let fieldData = formDataObj.formData?.[field.id];
              
              let fieldValue = '';
              
              if (field.type === 'name') {
                fieldValue = {
                  firstName: fieldData?.value?.firstName || '',
                  lastName: fieldData?.value?.lastName || ''
                };
              } else if (field.type === 'table') {
                // Tablo alanı için özel işlem
                if (fieldData?.value && fieldData.value.rows) {
                  fieldValue = fieldData.value;
                } else {
                  fieldValue = { rows: [] };
                }
              } else if (field.type === 'signature') {
                // İmza alanı için özel işlem - farklı formatları kontrol et
                fieldValue = fieldData?.value || 
                            formDataObj.signature || 
                            formDataObj.formData?.signature ||
                            formDataObj[field.id]?.value ||
                            '';
              } else {
                // Normal alanlar için değeri al
                fieldValue = fieldData?.value || '';
              }
              
              return {
                ...field,
                value: fieldValue,
                label: field.label || '',
                type: field.type || 'text',
                options: field.options || [],
                required: field.required || false,
                placeholder: field.placeholder || '',
                helpText: field.helpText || '',
                size: field.size || '12'
              };
            });

            setFormData({
              id: apiResponseData.id,
              formId: formContentObj.id?.toString() || apiResponseData.formId,
              formTitle: apiResponseData.title || formContentObj.title || 'Form Yanıtı',
              formDescription: formContentObj.description || '',
              formFields: fieldsWithValues,
              settings: formContentObj.settings || {}
            });
          } else {
            console.log(`API'de response ID ${responseId} bulunamadı`);
            console.log('Mevcut response ID\'leri:', apiData.formLibraryRecords.map(r => r.id));
            setError(`Yanıt bulunamadı (Response ID: ${responseId})`);
          }
        } else {
          console.log('API yanıtında formLibraryRecords bulunamadı');
          setError('Yanıt verileri bulunamadı');
        }
      } catch (error) {
        console.error('Yanıt yüklenirken hata:', error);
        setError('Yanıt yüklenirken hata oluştu');
      } finally {
        setLoading(false);
      }
    };

    loadResponseData();
  }, [responseId]);

  const handleBack = () => {
    // localStorage'ı temizle
    localStorage.removeItem('currentResponseId');
    localStorage.removeItem('currentResponseData');
    localStorage.removeItem('currentFormContent');
    localStorage.removeItem('currentFormData');
    localStorage.removeItem('currentFormTitle');
    
    navigate('/user-dashboard');
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      const formFields = formData.formFields.map(field => {
        let fieldValue = field.value;
        
        if (["file", "document", "image"].includes(field.type)) {
          fieldValue = Array.isArray(field.value) ? field.value : [];
        } else if (field.type === 'calculation') {
          fieldValue = typeof field.value === 'object' ? JSON.stringify(field.value) : field.value;
        } else if (field.type === 'name') {
          fieldValue = {
            firstName: field.value.firstName || '',
            lastName: field.value.lastName || ''
          };
        } else if (field.type === 'signature') {
          // İmza alanı için özel işlem - değeri koru
          fieldValue = field.value || '';
        }
        
        return {
          id: field.id,
          type: field.type,
          label: field.label,
          value: fieldValue
        };
      });

      // Kullanıcı bilgilerini al
      const userInfo = {
        id: userData?.id,
        name: userData?.userData?.name || userData?.username || 'Kullanıcı',
        email: userData?.userData?.email || userData?.username || '',
        categories: userData?.userData?.categories,
        isActive: userData?.userData?.isActive !== false,
        token: userData?.userData?.token || userData?.token
      };

      const submissionData = {
        id: formData.id,
        formId: formData.formId,
        formContent: {
          id: parseInt(formData.formId),
          title: formData.formTitle,
          description: formData.formDescription,
          fields: formData.formFields,
          settings: formData.settings
        },
        formData: {
          formId: formData.formId,
          formData: formFields.reduce((acc, field) => {
            acc[field.id] = {
              value: field.value,
              label: field.label,
              type: field.type
            };
            return acc;
          }, {})
        },
        user: userInfo.name || userInfo.email || 'Kullanıcı' // Kullanıcı adını string olarak gönder
      };

      console.log('Güncellenecek veri:', submissionData);

      const response = await axios.put(
        `${API_BASE_URL}/form/LibraryRecords/${formData.id}`,
        submissionData,
        {
          headers: API_HEADERS,
          timeout: 10000
        }
      );

      if (response.status === 200) {
        console.log('Form başarıyla güncellendi:', response.data);
        setIsEditing(false);
        // Sayfayı yenile
        window.location.reload();
      }
    } catch (error) {
      console.error('Form güncelleme hatası:', error);
      setError('Form güncellenirken bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setSaving(false);
    }
  };

  const handleFieldChange = (fieldId, value) => {
    setFormData(prev => ({
      ...prev,
      formFields: prev.formFields.map(field => 
        field.id === fieldId ? { ...field, value } : field
      )
    }));
  };

  const renderField = (field) => {
    const fieldValue = field.value;
    
    if (isEditing) {
      // Düzenleme modu
      switch (field.type) {
        case 'text':
        case 'email':
        case 'number':
        case 'tel':
          return (
            <TextField
              key={field.id}
              fullWidth
              label={field.label}
              value={fieldValue || ''}
              onChange={(e) => handleFieldChange(field.id, e.target.value)}
              variant="outlined"
              size="small"
              sx={{ mb: 2 }}
            />
          );

        case 'textarea':
          return (
            <TextField
              key={field.id}
              fullWidth
              label={field.label}
              value={fieldValue || ''}
              onChange={(e) => handleFieldChange(field.id, e.target.value)}
              variant="outlined"
              multiline
              rows={4}
              size="small"
              sx={{ mb: 2 }}
            />
          );

        case 'select':
          return (
            <FormControl key={field.id} fullWidth size="small" sx={{ mb: 2 }}>
              <Select
                value={fieldValue || ''}
                onChange={(e) => handleFieldChange(field.id, e.target.value)}
                displayEmpty
              >
                <MenuItem value="">
                  <em>Seçiniz</em>
                </MenuItem>
                {field.options?.map((option, index) => (
                  <MenuItem key={index} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>{field.label}</FormHelperText>
            </FormControl>
          );

        case 'date':
          return (
            <TextField
              key={field.id}
              fullWidth
              label={field.label}
              type="date"
              value={fieldValue || ''}
              onChange={(e) => handleFieldChange(field.id, e.target.value)}
              variant="outlined"
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 2 }}
            />
          );

        case 'name':
          return (
            <Box key={field.id} sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                {field.label}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Ad"
                  value={fieldValue?.firstName || ''}
                  onChange={(e) => handleFieldChange(field.id, {
                    ...fieldValue,
                    firstName: e.target.value
                  })}
                  variant="outlined"
                  size="small"
                />
                <TextField
                  fullWidth
                  label="Soyad"
                  value={fieldValue?.lastName || ''}
                  onChange={(e) => handleFieldChange(field.id, {
                    ...fieldValue,
                    lastName: e.target.value
                  })}
                  variant="outlined"
                  size="small"
                />
              </Box>
            </Box>
          );

        case 'table':
          // Tablo alanları düzenlenemez olarak göster
          return (
            <Box key={field.id} sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                {field.label}
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                Tablo alanları düzenlenemez. Sadece görüntüleme modu mevcuttur.
              </Alert>
              {renderTableDisplay(fieldValue)}
            </Box>
          );

        default:
          return (
            <TextField
              key={field.id}
              fullWidth
              label={field.label}
              value={fieldValue || ''}
              onChange={(e) => handleFieldChange(field.id, e.target.value)}
              variant="outlined"
              size="small"
              sx={{ mb: 2 }}
            />
          );
      }
    } else {
      // Görüntüleme modu
      if (field.type === 'table') {
        return (
          <Box key={field.id} sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              {field.label}
            </Typography>
            {renderTableDisplay(fieldValue)}
          </Box>
        );
      }

      let displayValue = fieldValue;
      
      if (typeof fieldValue === 'object' && fieldValue !== null) {
        if (fieldValue.firstName && fieldValue.lastName) {
          displayValue = `${fieldValue.firstName} ${fieldValue.lastName}`;
        } else if (fieldValue.firstName) {
          displayValue = fieldValue.firstName;
        } else {
          displayValue = JSON.stringify(fieldValue);
        }
      }
      
      return (
        <Box key={field.id} sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {field.label}
          </Typography>
          <Typography variant="body1" sx={{ 
            p: 2, 
            bgcolor: 'grey.50', 
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'grey.300'
          }}>
            {displayValue || '-'}
          </Typography>
        </Box>
      );
    }
  };

  const renderTableDisplay = (tableValue) => {
    if (!tableValue || !tableValue.rows || tableValue.rows.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          Tablo verisi bulunamadı
        </Typography>
      );
    }

    const rows = tableValue.rows;
    const firstRow = rows[0];
    let columnHeaders = [];
    
    // Kolon başlıklarını belirle
    if (Array.isArray(firstRow)) {
      columnHeaders = firstRow.map((_, index) => `Kolon ${index + 1}`);
    } else if (typeof firstRow === 'object' && firstRow !== null) {
      columnHeaders = Object.keys(firstRow);
    }

    return (
      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
        <Table size="small" stickyHeader>
          {columnHeaders.length > 0 && (
            <thead>
              <tr>
                {columnHeaders.map((header, idx) => (
                  <TableCell 
                    key={idx} 
                    sx={{ 
                      fontWeight: 'bold', 
                      bgcolor: '#f5f5f5',
                      borderRight: '1px solid #ddd'
                    }}
                  >
                    {header}
                  </TableCell>
                ))}
              </tr>
            </thead>
          )}
          <TableBody>
            {rows.map((row, rowIdx) => (
              <tr key={rowIdx}>
                {Array.isArray(row) 
                  ? row.map((cell, cellIdx) => (
                      <TableCell 
                        key={cellIdx}
                        sx={{ borderRight: '1px solid #ddd' }}
                      >
                        {cell || '-'}
                      </TableCell>
                    ))
                  : columnHeaders.map((header, cellIdx) => (
                      <TableCell 
                        key={cellIdx}
                        sx={{ borderRight: '1px solid #ddd' }}
                      >
                        {row[header] || '-'}
                      </TableCell>
                    ))
                }
              </tr>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column',
        gap: 2
      }}>
        <CircularProgress size={60} />
        <Typography variant="body1">
          Yanıt yükleniyor...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column',
        gap: 2
      }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={handleBack}>
          Geri Dön
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {/* Mobil AppBar */}
      {isMobile && (
        <AppBar position="fixed" sx={{ background: 'white', color: 'black' }}>
          <Toolbar>
            <IconButton edge="start" onClick={handleBack}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" sx={{ ml: 1, flex: 1 }}>
              {isEditing ? 'Yanıt Düzenle' : 'Yanıt Detayı'}
            </Typography>
            {!isEditing && (
              <IconButton onClick={handleEdit}>
                <EditIcon />
              </IconButton>
            )}
          </Toolbar>
        </AppBar>
      )}

      <Container maxWidth="md" sx={{ pt: isMobile ? 8 : 2, pb: 2 }}>
        {/* Desktop Header */}
        {!isMobile && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              sx={{ mr: 2 }}
            >
              Geri Dön
            </Button>
            <Typography variant="h4" sx={{ fontWeight: 600, flex: 1 }}>
              {isEditing ? 'Yanıt Düzenle' : 'Yanıt Detayı'}
            </Typography>
            {!isEditing && (
              <Button
                startIcon={<EditIcon />}
                onClick={handleEdit}
                variant="outlined"
                sx={{ mr: 2 }}
              >
                Düzenle
              </Button>
            )}
            {isEditing && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  variant="contained"
                  disabled={saving}
                >
                  {saving ? 'Kaydediliyor...' : 'Kaydet'}
                </Button>
                <Button
                  startIcon={<CloseIcon />}
                  onClick={handleCancel}
                  variant="outlined"
                >
                  İptal
                </Button>
              </Box>
            )}
          </Box>
        )}

        {formData && (
          <>
            {/* Yanıt Başlığı */}
            <Paper elevation={1} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AssessmentIcon sx={{ mr: 2, color: 'primary.main' }} />
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {formData.formTitle}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Gönderim Tarihi: {new Date(responseData?.createDate || new Date()).toLocaleDateString('tr-TR')} - {new Date(responseData?.createDate || new Date()).toLocaleTimeString('tr-TR')}
                  </Typography>
                </Box>
              </Box>
            </Paper>

            {/* Form İçeriği */}
            {formData.formFields && (
              <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                  {isEditing ? 'Form Yanıtlarını Düzenle' : 'Form Yanıtları'}
                </Typography>
                
                <Box sx={{ display: 'grid', gap: 2 }}>
                  {formData.formFields.map((field) => renderField(field))}
                </Box>
              </Paper>
            )}
          </>
        )}
      </Container>
    </Box>
  );
};

export default FormResponseDetail; 